<template>
    <div>
        <!-- Page Header -->
        <section class="bg-dark pt-5 pb-4">
            <div class="container py-5">
                <h1 class="text-light pt-1 pt-md-3 mb-4">
                    Sage Accounting Integration
                </h1>
                <p class="text-light opacity-75 mb-0">
                    Connect your Sage Accounting account to import clients for quick referrals
                </p>
            </div>
        </section>
        <!-- / Page Header -->

        <!-- Integration Content -->
        <section class="position-relative bg-white rounded-xxl-4 zindex-5" style="margin-top: -30px;">
            <div class="container pt-4 pb-5 mb-md-4">
                <!-- Breadcrumb-->
                <breadcrumb :crumbs="crumbs" class="pb-4 my-2"></breadcrumb>

                <!-- Is Loading -->
                <div v-if="isLoading" class="text-center">
                    <spinner></spinner>
                </div>
                <!-- / Is Loading -->

                <!-- Integration Status -->
                <div v-if="!isLoading" class="row">
                    <div class="col-lg-8">
                        <!-- Integration Card -->
                        <div class="card border-0 shadow-sm">
                            <div class="card-body p-4">
                                <div class="d-flex align-items-center mb-4">
                                    <div class="flex-shrink-0 me-3">
                                        <div class="rounded-3" style="width: 48px; height: 48px; overflow: hidden;">
                                            <img src="/img/sage.png" alt="Sage" style="width: 100%; height: 100%; object-fit: cover;">
                                        </div>
                                    </div>
                                    <div class="flex-grow-1">
                                        <h4 class="mb-1">Sage Accounting</h4>
                                        <p class="text-muted mb-0">Import clients for quick referrals</p>
                                    </div>
                                    <div class="flex-shrink-0">
                                        <span v-if="integrationStatus === 'connected'" 
                                              class="badge bg-success bg-opacity-10 text-success">
                                            <i class="fi-check-circle me-1"></i>
                                            Connected
                                        </span>
                                        <span v-else-if="integrationStatus === 'disconnected'" 
                                              class="badge bg-secondary bg-opacity-10 text-secondary">
                                            <i class="fi-alert-circle me-1"></i>
                                            Disconnected
                                        </span>
                                        <span v-else 
                                              class="badge bg-secondary bg-opacity-10 text-secondary">
                                            <i class="fi-clock me-1"></i>
                                            Checking...
                                        </span>
                                    </div>
                                </div>

                                <!-- Connection Status -->
                                <div v-if="integrationStatus === 'connected'" class="mb-4">
                                    <div class="alert alert-success">
                                        <h6 class="alert-heading">
                                            <i class="fi-check-circle me-2"></i>
                                            Successfully Connected
                                        </h6>
                                        <p class="mb-2">Your Sage Accounting account is connected and ready to import clients for referrals.</p>
                                        <div class="row">
                                            <div class="col-md-6">
                                                <small class="text-muted">
                                                    <strong>Business ID:</strong> {{ (integrationDetails && integrationDetails.business_id) || 'Unknown' }}
                                                </small>
                                            </div>
                                            <div class="col-md-6">
                                                <small class="text-muted">
                                                    <strong>Scope:</strong> {{ (integrationDetails && integrationDetails.scope) || 'Full Access' }}
                                                </small>
                                            </div>
                                        </div>
                                        <div class="row mt-2">
                                            <div class="col-md-6">
                                                <small class="text-muted">
                                                    <strong>Last Sync:</strong> {{ formatLastSync() }}
                                                </small>
                                            </div>
                                            <div class="col-md-6">
                                                <small class="text-muted">
                                                    <strong>Token Expires:</strong> {{ formatTokenExpiry() }}
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Disconnected Status -->
                                <div v-else-if="integrationStatus === 'disconnected'" class="mb-4">
                                    <div class="alert alert-secondary">
                                        <h6 class="alert-heading">
                                            <i class="fi-alert-circle me-2"></i>
                                            Not Connected
                                        </h6>
                                        <p class="mb-0">Connect your Sage Accounting account to import clients for quick referrals and client management.</p>
                                    </div>
                                </div>

                                <!-- Error Status -->
                                <div v-else-if="integrationStatus === 'error'" class="mb-4">
                                    <div class="alert alert-danger">
                                        <h6 class="alert-heading">
                                            <i class="fi-x-circle me-2"></i>
                                            Connection Error
                                        </h6>
                                        <p class="mb-0">There was an error checking your integration status. Please try again.</p>
                                    </div>
                                </div>

                                <!-- Integration Actions -->
                                <div class="d-flex gap-2">
                                    <button v-if="integrationStatus === 'disconnected'" 
                                            @click="connectSage" 
                                            class="btn btn-primary"
                                            :disabled="isConnecting">
                                        <spinner v-if="isConnecting" class="me-2"></spinner>
                                        <i v-else class="fi-link me-2"></i>
                                        {{ isConnecting ? 'Connecting...' : 'Connect Sage Account' }}
                                    </button>
                                    
                                    <button v-if="integrationStatus === 'connected'" 
                                            @click="disconnectSage" 
                                            class="btn btn-outline-danger"
                                            :disabled="isDisconnecting">
                                        <spinner v-if="isDisconnecting" class="me-2"></spinner>
                                        <i v-else class="fi-unlink me-2"></i>
                                        {{ isDisconnecting ? 'Disconnecting...' : 'Disconnect Sage' }}
                                    </button>
                                    
                                    <button v-if="integrationStatus === 'connected'" 
                                            @click="refreshData" 
                                            class="btn btn-outline-primary"
                                            :disabled="isRefreshing">
                                        <spinner v-if="isRefreshing" class="me-2"></spinner>
                                        <i v-else class="fi-refresh me-2"></i>
                                        {{ isRefreshing ? 'Refreshing...' : 'Refresh Data' }}
                                    </button>
                                    
                                    <button v-if="integrationStatus === 'error'" 
                                            @click="checkIntegrationStatus" 
                                            class="btn btn-outline-secondary">
                                        <i class="fi-refresh me-2"></i>
                                        Retry
                                    </button>
                                </div>
                            </div>
                        </div>
                        <!-- / Integration Card -->

                    </div>

                    <!-- Sidebar -->
                    <div class="col-lg-4">
                        <div class="card border-0 shadow-sm">
                            <div class="card-body p-4">
                                <h5 class="mb-3">Need Help?</h5>
                                <p class="text-muted mb-3">
                                    Having trouble connecting your Sage account?
                                </p>
                                <a href="https://syndesi.network/contact/" 
                                   target="_blank" 
                                   class="btn btn-outline-primary">
                                    <i class="fi-help me-2"></i>
                                    Contact Support
                                </a>
                            </div>
                        </div>
                    </div>
                    <!-- / Sidebar -->
                </div>
                <!-- / Integration Status -->

            </div>
        </section>
        <!-- / Integration Content -->
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Spinner from '../../components/Spinner.vue';
import Breadcrumb from '../../components/Breadcrumb.vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';

const toast = useToast();

export default {
    name: 'SageIntegration',
    
    components: { 
        Spinner, 
        Breadcrumb 
    },

    data() {
        return {
            isLoading: false,
            isConnecting: false,
            isDisconnecting: false,
            isRefreshing: false,
            integrationStatus: 'checking', // checking, connected, disconnected, error
            integrationDetails: null
        }
    },

    computed: {
        ...mapGetters('auth', {
            user: 'user',
            userRole: 'userRole',
            isAdvisor: 'isAdvisor'
        }),

        crumbs() {
            return [
                {
                    href: "/",
                    label: "Dashboard",
                },
                {
                    href: "/integrations",
                    label: "Integrations",
                },
                {
                    href: "#",
                    label: "Sage Accounting",
                    active: true,
                },
            ];
        }
    },

    created() {
        // Check if user is an advisor, if not redirect
        if (!this.isAdvisor) {
            this.$router.push('/');
            return;
        }

        this.checkIntegrationStatus();
    },

    methods: {
        async checkIntegrationStatus() {
            if (!this.userRole) return;

            this.isLoading = true;
            this.integrationStatus = 'checking';

            try {
                const response = await axios.get(`${window.API_BASE}/sage/check-integration`, {
                    params: {
                        user_role_id: this.userRole.id
                    },
                    headers: {
                        'Authorization': `Bearer ${this.$store.getters['auth/apiToken']}`
                    }
                });

                if (response.data.has_integration) {
                    this.integrationStatus = 'connected';
                    this.integrationDetails = response.data.integration_details;
                } else {
                    this.integrationStatus = 'disconnected';
                    this.integrationDetails = null;
                }
            } catch (error) {
                console.error('Failed to check integration status:', error);
                this.integrationStatus = 'error';
                this.integrationDetails = null;
            } finally {
                this.isLoading = false;
            }
        },

        async connectSage() {
            if (!this.userRole) return;

            this.isConnecting = true;

            try {
                const response = await axios.post(`${window.API_BASE}/sage/authorization-url`, {
                    user_role_id: this.userRole.id
                }, {
                    headers: {
                        'Authorization': `Bearer ${this.$store.getters['auth/apiToken']}`,
                        'Content-Type': 'application/json'
                    }
                });

                // Redirect to Sage authorization
                window.location.href = response.data.authorization_url;
            } catch (error) {
                console.error('Failed to initiate Sage connection:', error);
                toast.error('Failed to start Sage connection. Please try again.');
            } finally {
                this.isConnecting = false;
            }
        },

        async disconnectSage() {
            if (!this.userRole) return;

            this.isDisconnecting = true;

            try {
                const response = await axios.post(`${window.API_BASE}/sage/deactivate`, {
                    user_role_id: this.userRole.id
                }, {
                    headers: {
                        'Authorization': `Bearer ${this.$store.getters['auth/apiToken']}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.data.success) {
                    this.integrationStatus = 'disconnected';
                    this.integrationDetails = null;
                    toast.success('Sage integration disconnected successfully');
                } else {
                    toast.warning('No active integration to disconnect');
                }
            } catch (error) {
                console.error('Failed to disconnect Sage:', error);
                toast.error('Failed to disconnect Sage integration');
            } finally {
                this.isDisconnecting = false;
            }
        },

        async refreshData() {
            if (!this.userRole) return;

            this.isRefreshing = true;

            try {
                // This would be a call to refresh/update the integration data
                // For now, we'll just recheck the status
                await this.checkIntegrationStatus();
                toast.success('Integration data refreshed successfully');
            } catch (error) {
                console.error('Failed to refresh data:', error);
                toast.error('Failed to refresh integration data');
            } finally {
                this.isRefreshing = false;
            }
        },

        formatLastSync() {
            if (!this.integrationDetails || !this.integrationDetails.last_sync_at) {
                return 'Never';
            }
            return new Date(this.integrationDetails.last_sync_at).toLocaleDateString();
        },

        formatTokenExpiry() {
            if (!this.integrationDetails || !this.integrationDetails.token_expires_at) {
                return 'Unknown';
            }
            return new Date(this.integrationDetails.token_expires_at).toLocaleDateString();
        }
    }
}
</script>

<style scoped>
.card {
    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.card:hover {
    transform: translateY(-2px);
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}
</style> 